<template>
  <form class="rm-screen cmm-topup" novalidate @submit.prevent="submitTopup">
    <!-- 1. Limit -->
    <section class="rm-card cmm-step" aria-labelledby="cmm-h-limit">
      <h2 id="cmm-h-limit" class="cmm-step__head">
        <span class="cmm-step__n" aria-hidden="true">1</span>
        {{ $t("limit") }}
      </h2>
      <div v-if="limitsLoading" class="cmm-choices" aria-busy="true">
        <div class="rm-skeleton cmm-skeleton"></div>
      </div>
      <p v-else-if="!limitCards.length" class="rm-muted cmm-note">{{ $t("company_report.topup.no_limits") }}</p>
      <div v-else class="cmm-choices" role="radiogroup" aria-labelledby="cmm-h-limit">
        <label v-for="card in limitCards" :key="card.id" class="cmm-choice" :class="{ 'is-on': isLimitOn(card) }">
          <input
            type="radio"
            name="cmm-limit"
            class="rm-visually-hidden"
            :checked="isLimitOn(card)"
            @change="selectedLimit = card.option"
          />
          <span class="cmm-choice__text">
            <b>{{ card.name }}</b>
            <span v-if="card.detail" class="rm-muted">{{ card.detail }}</span>
            <span class="rm-muted">{{ card.scope }}</span>
          </span>
          <v-icon class="cmm-choice__tick" size="22">mdi-check-circle</v-icon>
        </label>
      </div>
    </section>

    <!-- 2. Radnici -->
    <section class="rm-card cmm-step" aria-labelledby="cmm-h-workers">
      <h2 id="cmm-h-workers" class="cmm-step__head">
        <span class="cmm-step__n" aria-hidden="true">2</span>
        {{ $t("employees") }}
        <span class="cmm-step__meta rm-num">
          {{ $t("company_report.topup.selected", { n: selectedUsers.length, total: users.length }) }}
        </span>
      </h2>
      <div v-if="usersLoading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <p v-else-if="!users.length" class="rm-muted cmm-note">{{ $t("company_report.topup.no_workers") }}</p>
      <template v-else>
        <label class="rm-search">
          <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
          <span class="rm-visually-hidden">{{ $t("company_report.topup.search") }}</span>
          <input
            v-model="workerSearch"
            type="search"
            :placeholder="$t('company_report.topup.search')"
            autocomplete="off"
            @keydown.enter.prevent
          />
        </label>
        <div class="rm-chip-row cmm-tools">
          <button type="button" class="rm-chip" :disabled="!shownUsers.length" @click="selectShown">
            {{ $t("select_all") }}
          </button>
          <button type="button" class="rm-chip" :disabled="!selectedUsers.length" @click="selectedUsers = []">
            {{ $t("company_report.topup.deselect") }}
          </button>
        </div>
        <ul v-if="shownUsers.length" ref="workerList" class="cmm-workers">
          <li v-for="user in shownUsers" :key="user.id">
            <label class="cmm-worker" :class="{ 'is-on': isSelected(user.id) }">
              <input v-model="selectedUsers" type="checkbox" class="rm-visually-hidden" :value="user.id" />
              <v-icon size="22">{{ isSelected(user.id) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}</v-icon>
              <span class="cmm-worker__name">{{ user.name }}</span>
            </label>
          </li>
        </ul>
        <p v-else class="rm-muted cmm-note">{{ $t("company_report.topup.no_match") }}</p>
      </template>
    </section>

    <!-- 3. Iznos -->
    <section class="rm-card cmm-step" aria-labelledby="cmm-h-amount">
      <h2 id="cmm-h-amount" class="cmm-step__head">
        <span class="cmm-step__n" aria-hidden="true">3</span>
        {{ $t("amount") }}
      </h2>
      <label class="cmm-money-input" :class="{ 'is-error': Boolean(amountError) }">
        <input
          v-model="topupAmount"
          type="number"
          inputmode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          :aria-label="$t('amount')"
          :aria-invalid="String(Boolean(amountError))"
        />
        <span class="cmm-money-input__cur">{{ currency }}</span>
      </label>
      <p v-if="amountError" class="cmm-error" role="alert">{{ amountError }}</p>
      <div class="rm-chip-row cmm-quick" role="group" :aria-label="$t('quick_amounts')">
        <button
          v-for="quick in quickAmounts"
          :key="quick"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': amountValue === quick }"
          :aria-pressed="String(amountValue === quick)"
          @click="setQuickAmount(quick)"
        >
          {{ quick }} {{ currency }}
        </button>
      </div>
    </section>

    <!-- 4. Uplata -->
    <section class="rm-card cmm-step" aria-labelledby="cmm-h-payment">
      <h2 id="cmm-h-payment" class="cmm-step__head">
        <span class="cmm-step__n" aria-hidden="true">4</span>
        {{ $t("company_report.topup.payment") }}
      </h2>
      <fieldset class="cmm-field">
        <legend>{{ $t("topped_by") }}</legend>
        <div class="rm-chip-row">
          <button
            v-for="option in toppedByOptions"
            :key="option.value"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--active': toppedBy === option.value }"
            :aria-pressed="String(toppedBy === option.value)"
            @click="toppedBy = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </fieldset>
      <fieldset class="cmm-field">
        <legend>{{ $t("payment_method") }}</legend>
        <div class="rm-chip-row">
          <button
            v-for="option in paymentMethods"
            :key="option.value"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--active': paymentMethod === option.value }"
            :aria-pressed="String(paymentMethod === option.value)"
            @click="paymentMethod = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </fieldset>
    </section>

    <div v-if="recent.length" class="rm-card cmm-step cmm-recent">
      <h2 class="cmm-step__head">{{ $t("company_report.topup.recent") }}</h2>
      <ul>
        <li v-for="item in recent" :key="item.key">
          <v-icon size="16" color="#15803d">mdi-check-circle</v-icon>
          <span class="cmm-recent__name">{{ item.name }}</span>
          <b class="rm-num">{{ money(item.amount) }} {{ currency }}</b>
        </li>
      </ul>
    </div>

    <!-- Pregled i dugme uvek na dnu -->
    <div class="cmm-bar">
      <div v-if="submitError" class="cmm-alert" role="alert">{{ submitError }}</div>
      <div v-else-if="notice" class="cmm-notice" role="status">{{ notice }}</div>
      <p v-else-if="missing.length" class="cmm-missing">
        {{ $t("company_report.topup.missing", { list: missing.join(", ") }) }}
      </p>
      <div class="cmm-bar__total">
        <span class="rm-muted rm-num">{{ selectedUsers.length }} × {{ money(amountValue) }} {{ currency }}</span>
        <b class="rm-num">{{ money(total) }} {{ currency }}</b>
      </div>
      <button type="submit" class="rm-btn rm-btn--primary cmm-bar__submit" :disabled="!canSubmit">
        <v-icon size="20" color="#ffffff" :class="{ 'is-spinning': submitting }">
          {{ submitting ? "mdi-loading" : "mdi-cash-plus" }}
        </v-icon>
        {{ $t("add_credit") }}
      </button>
    </div>
  </form>
</template>

<script>
import CompanyDesktopMultiTopup from "@/components/company-report/desktop/screens/multi/CompanyDesktopMultiTopup.vue";
import hasMultiMobile from "../../hasMultiMobile";

/**
 * Mobilni "Dodaj kredit" firme "više restorana": isti koraci kao na računaru (limit, radnici, iznos, uplata), jedan
 * ispod drugog, a ukupan iznos i dugme su uvek na dnu ekrana. Radnici, limiti, provera i slanje su u ekranu za
 * računar (nasleđeno), pa su pravila ista: pri delimičnom neuspehu ostaju izabrani samo radnici kojima nije uspelo.
 */
export default {
  name: "CompanyMobileMultiTopup",
  extends: CompanyDesktopMultiTopup,
  mixins: [hasMultiMobile],
};
</script>
